// Frontend logging seam (DEN-141) — the single client-side module frontend
// code calls to report a client error event. Browser-side counterpart to
// `src/logger.ts`; never talks to the log store directly, forwards via
// `POST /api/log` (src/routes/log.ts), re-emitted through the server pino
// logger. See CONTEXT.md "Frontend logging seam" / "Client error event".

const SEND_CAP = 3;

// Per-tab-lifetime dedupe state — resets on reload, no timers.
const sendCounts = new Map<string, number>();

let currentUserId: string | undefined;

/** Called by `AuthProvider` whenever the signed-in user changes. */
export function setLoggingUserId(userId: string | undefined): void {
  currentUserId = userId;
}

export interface UnhandledErrorPayload {
  source: 'error' | 'unhandledrejection';
  message: string;
  stack?: string;
}

export interface I18nMissingKeyPayload {
  ns: string;
  key: string;
}

export interface ApiFailurePayload {
  method: string;
  path: string;
  status: number;
  message: string;
  requestId?: string;
}

export interface ReactErrorBoundaryPayload {
  message: string;
  stack?: string;
  componentStack?: string;
}

export type ClientErrorEvent =
  | ({ kind: 'unhandled-error' } & UnhandledErrorPayload)
  | ({ kind: 'i18n-missing-key' } & I18nMissingKeyPayload)
  | ({ kind: 'api-failure' } & ApiFailurePayload)
  | ({ kind: 'react-error-boundary' } & ReactErrorBoundaryPayload);

// Regex denylist scrub applied to message/stack/componentStack before any
// send — email pattern at minimum (DEN-141).
const SCRUB_PATTERNS: RegExp[] = [/[\w.+-]+@[\w-]+\.[\w.-]+/g];

function scrub(value: string): string {
  return SCRUB_PATTERNS.reduce(
    (scrubbed, pattern) => scrubbed.replace(pattern, '[redacted]'),
    value,
  );
}

const SCRUBBED_FIELDS = ['message', 'stack', 'componentStack'] as const;

function scrubEvent(event: ClientErrorEvent): ClientErrorEvent {
  const scrubbed: Record<string, unknown> = { ...event };
  for (const field of SCRUBBED_FIELDS) {
    const value = scrubbed[field];
    if (typeof value === 'string') {
      scrubbed[field] = scrub(value);
    }
  }
  return scrubbed as unknown as ClientErrorEvent;
}

function firstLine(value: string | undefined): string {
  return (value ?? '').split('\n', 1)[0];
}

// Dedupe key: kind + message + first line of stack/componentStack. No `url`
// in the key (DEN-141).
function dedupeKey(event: ClientErrorEvent): string {
  const message =
    'message' in event ? event.message : `${event.ns}:${event.key}`;
  const stackLine =
    'stack' in event
      ? firstLine(event.stack)
      : 'componentStack' in event
        ? firstLine(event.componentStack)
        : '';
  return `${event.kind} ${message} ${stackLine}`;
}

function send(event: ClientErrorEvent): void {
  const body = currentUserId ? { ...event, userId: currentUserId } : event;
  fetch('/api/log', {
    method: 'POST',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => {
    // Fire-and-forget: no retry, doesn't distinguish response status.
  });
}

/**
 * Reports a client error event. Console output is unconditional in every
 * environment; the network send is scrubbed, deduped (cap ~3 sends per
 * dedupe-key per tab lifetime, then silently discarded), and skipped
 * entirely in development.
 */
function reportEvent(event: ClientErrorEvent): void {
  console.error(`[client error event] ${event.kind}`, event);

  if (process.env.NODE_ENV === 'development') {
    return;
  }

  const scrubbed = scrubEvent(event);
  const key = dedupeKey(scrubbed);
  const count = sendCounts.get(key) ?? 0;
  if (count >= SEND_CAP) {
    return;
  }
  sendCounts.set(key, count + 1);
  send(scrubbed);
}

/**
 * Public seam entry point for the taxonomy's non-automatic event kinds
 * (`unhandled-error` is reported automatically by
 * `installGlobalErrorReporting` below, never called directly).
 */
export function reportError(
  kind: 'i18n-missing-key',
  payload: I18nMissingKeyPayload,
): void;
export function reportError(
  kind: 'api-failure',
  payload: ApiFailurePayload,
): void;
export function reportError(
  kind: 'react-error-boundary',
  payload: ReactErrorBoundaryPayload,
): void;
export function reportError(
  kind: 'i18n-missing-key' | 'api-failure' | 'react-error-boundary',
  payload: I18nMissingKeyPayload | ApiFailurePayload | ReactErrorBoundaryPayload,
): void {
  reportEvent({ kind, ...payload } as ClientErrorEvent);
}

function reportUnhandledError(
  source: 'error' | 'unhandledrejection',
  reason: unknown,
): void {
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;
  reportEvent({ kind: 'unhandled-error', source, message, stack });
}

let globalReportingInstalled = false;

const onWindowError = (event: ErrorEvent): void => {
  reportUnhandledError('error', event.error ?? event.message);
};
const onUnhandledRejection = (event: PromiseRejectionEvent): void => {
  reportUnhandledError('unhandledrejection', event.reason);
};

/**
 * Wires the global `error` + `unhandledrejection` window listeners. Call
 * once at app bootstrap (`main.tsx`) — idempotent, so a duplicate call
 * (e.g. from a test) never double-reports.
 */
export function installGlobalErrorReporting(): void {
  if (globalReportingInstalled) {
    return;
  }
  globalReportingInstalled = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/** Test-only: restores the seam's initial state (user, dedupe counts, listeners). */
export function resetLogging(): void {
  currentUserId = undefined;
  sendCounts.clear();
  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onUnhandledRejection);
  globalReportingInstalled = false;
}

/** Test-only: snapshot read by the dirty-state guard in `setup.ts`. */
export function getLoggingState() {
  return {
    userId: currentUserId,
    sendCount: sendCounts.size,
    globalReportingInstalled,
  };
}
